import {
  Button,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiChevronDown } from "react-icons/fi";
import { useGetFilteredIfrsReportPreviewQuery } from "../../../autogen/bff-api";
import { useSub } from "../../../common/subscription/useSub";
import { downloadFilteredIfrsReport } from "../ifrsReportDownload";

export const ContractListDownloadMenu = ({ encodedQ }: { encodedQ: string }) => {
  const { hasIFRSAccess } = useSub();
  if (!hasIFRSAccess) return null;
  return <IfrsDownloadMenu encodedQ={encodedQ} />;
};

const IfrsDownloadMenu = ({ encodedQ }: { encodedQ: string }) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const {
    currentData: preview,
    isFetching,
    isError,
  } = useGetFilteredIfrsReportPreviewQuery(
    { q: encodedQ },
    { skip: !isMenuOpen && !isConfirming, refetchOnMountOrArgChange: true },
  );
  const nothingToReport = preview?.includedCount === 0;

  return (
    <>
      <Menu isLazy onOpen={() => setIsMenuOpen(true)} onClose={() => setIsMenuOpen(false)} placement="bottom-end">
        <MenuButton as={Button} size="sm" variant="outline" rightIcon={<FiChevronDown />}>
          {t("Download")}
        </MenuButton>
        <MenuList>
          <MenuItem
            isDisabled={isFetching || isError || !preview || nothingToReport}
            onClick={() => setIsConfirming(true)}
          >
            <Stack spacing="0">
              <Text>
                {t("IFRS 16 report (.xlsx)")} {isFetching && <Spinner size="xs" ml="1" />}
              </Text>
              {isError && !isFetching ? (
                <Text fontSize="xs" color="smError">
                  {t("Could not check the contracts. Close the menu and try again.")}
                </Text>
              ) : (
                nothingToReport && (
                  <Text fontSize="xs" color="smMuted">
                    {t("None of the contracts in the list have filled-in IFRS values")}
                  </Text>
                )
              )}
            </Stack>
          </MenuItem>
        </MenuList>
      </Menu>
      {isConfirming && preview && (
        <IfrsReportConfirmation
          encodedQ={encodedQ}
          includedCount={preview.includedCount}
          missing={preview.missingIfrsValues.map((c) => ({ id: c.id, title: c.title }))}
          onClose={() => setIsConfirming(false)}
        />
      )}
    </>
  );
};

const IfrsReportConfirmation = ({
  encodedQ,
  includedCount,
  missing,
  onClose,
}: {
  encodedQ: string;
  includedCount: number;
  missing: { id: string; title: string }[];
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const download = async () => {
    setIsDownloading(true);
    try {
      await downloadFilteredIfrsReport(encodedQ);
      onClose();
    } catch {
      toast({ description: t("The report could not be downloaded. Try again."), status: "error" });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="lg" closeOnOverlayClick={!isDownloading} closeOnEsc={!isDownloading}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("IFRS 16 report")}</ModalHeader>
        <ModalCloseButton isDisabled={isDownloading} />
        <ModalBody>
          <Stack spacing="3">
            <Text>{t("The report includes {{count}} contracts.", { count: includedCount })}</Text>
            {missing.length > 0 && (
              <Stack spacing="1">
                <Text>
                  {t("{{count}} contracts are missing IFRS values and are left out:", { count: missing.length })}
                </Text>
                <List spacing="0.5" maxH="48" overflowY="auto" fontSize="sm" color="smMuted">
                  {missing.map((c) => (
                    <ListItem key={c.id}>{c.title}</ListItem>
                  ))}
                </List>
              </Stack>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap="2">
          <Button variant="ghost" onClick={onClose} isDisabled={isDownloading}>
            {t("Cancel")}
          </Button>
          <Button colorScheme="teal" onClick={download} isLoading={isDownloading}>
            {t("Download")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
